import React from 'react';
import { 
  ShieldCheck, Handshake, Globe, FileText, 
  Copyright, ShieldAlert, Cookie, Mail, Download, HeartHandshake, Zap, Scale
} from 'lucide-react';

const LegalSection = ({ title, icon: Icon, children }: { title: string, icon: any, children: React.ReactNode }) => (
  <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 hover:border-[#e0692d]/30 transition-all mb-8">
    <div className="flex items-center gap-3 mb-6 border-b border-slate-50 pb-4">
      <div className="p-2.5 bg-orange-50 rounded-xl text-[#e0692d]">
        <Icon className="w-6 h-6" />
      </div>
      <h2 className="text-xl font-black text-slate-900 tracking-tight">{title}</h2>
    </div>
    <div className="text-slate-600 leading-relaxed space-y-4 text-sm md:text-base">
      {children}
    </div>
  </div>
);

const MentionsLegales = () => {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* --- HEADER --- */}
      <div className="bg-slate-900 pt-32 pb-24 px-4 relative overflow-hidden text-center">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-[50%] -left-[10%] w-[500px] h-[500px] bg-[#e0692d] rounded-full mix-blend-multiply filter blur-[120px] opacity-10"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-orange-400 text-[10px] font-black mb-6 uppercase tracking-[0.2em]">
            <ShieldAlert className="w-3 h-3" /> Transparence & Éthique
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tighter">Mentions Légales</h1>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
            Cadre légal et engagements de la plateforme <span className="text-white font-bold">ServicePro.tn</span>.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 -mt-16 relative z-20">
        
        {/* --- NOUVEAUX BLOCS REMPLAÇANTS --- */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          
          {/* Bloc 1 : Engagement de Qualité */}
          <div className="bg-white p-10 rounded-[2.5rem] shadow-xl shadow-slate-200/50 border-b-8 border-orange-500">
            <div className="bg-orange-50 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 text-orange-600">
              <ShieldCheck size={32} />
            </div>
            <h2 className="text-2xl font-black text-slate-900 mb-4">Engagement Qualité</h2>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              Chaque professionnel inscrit sur ServicePro.tn s'engage à respecter une charte de qualité stricte : ponctualité, transparence tarifaire et respect des normes professionnelles en vigueur en Tunisie.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-500" /> Profils vérifiés par nos équipes
              </li>
              <li className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-500" /> Système d'avis clients authentiques
              </li>
            </ul>
          </div>

          {/* Bloc 2 : Éthique & Gratuité */}
          <div className="bg-[#1e293b] p-10 rounded-[2.5rem] shadow-xl shadow-slate-900/20 border-b-8 border-slate-700 text-white">
            <div className="bg-white/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 text-orange-400">
              <Scale size={32} />
            </div>
            <h2 className="text-2xl font-black mb-4">Éthique & Neutralité</h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Notre algorithme de mise en relation est neutre. Nous ne favorisons aucun prestataire, sauf dans le cadre du sponsoring explicitement mentionné "Annonce Sponsorisée". 
            </p>
            <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
              <p className="text-[11px] text-orange-200 font-bold uppercase tracking-widest mb-1">Notre Modèle</p>
              <p className="text-xs text-slate-300">Zéro commission sur vos contrats. Le sponsoring est notre unique source de revenu.</p>
            </div>
          </div>
        </div>

        {/* --- SECTIONS LEGALES CLASSIQUES --- */}
        <LegalSection title="Propriété Intellectuelle" icon={Copyright}>
          <p>
            L'architecture de la plateforme, les textes, graphismes, logos et icônes sont la propriété exclusive de <strong>ServicePro.tn</strong>. 
            Toute reproduction, représentation ou modification de tout ou partie des éléments du site est interdite sans autorisation préalable.
          </p>
        </LegalSection>

        <LegalSection title="Protection des Données (INPDP)" icon={ShieldAlert}>
          <p>
            Conformément à la loi tunisienne n° 2004-63, les informations collectées servent uniquement à faciliter la mise en relation entre Clients et Professionnels.
          </p>
          <div className="mt-4 p-5 bg-slate-900 rounded-2xl text-white flex flex-col md:flex-row gap-6 items-center justify-between">
            <div className="flex gap-4 items-center">
               <Mail className="w-6 h-6 text-orange-500" />
               <span className="text-sm font-medium">Une question sur vos données ?</span>
            </div>
            <a href="mailto:contact@servicepro.tn" className="bg-orange-600 hover:bg-orange-500 px-6 py-2 rounded-xl text-xs font-black transition-all">
              contact@servicepro.tn
            </a>
          </div>
        </LegalSection>

        <LegalSection title="Cookies & Traceurs" icon={Cookie}>
          <p>
            Nous utilisons uniquement des cookies essentiels à votre navigation et à la mesure d'audience anonyme. 
            Ces données nous aident à améliorer l'ergonomie de la plateforme pour les utilisateurs tunisiens.
          </p>
        </LegalSection>

        {/* --- FOOTER DOCUMENT --- */}
        <div className="flex flex-col md:flex-row justify-between items-center mt-16 pt-8 border-t border-slate-200 gap-6">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center text-white font-black text-xs">SP</div>
            <p className="text-slate-400 text-[11px] font-medium max-w-[200px]">
              © 2026 ServicePro Tunisie. Marketplace de services pour professionnels.
            </p>
          </div>
          <button className="flex items-center gap-2 text-slate-900 hover:bg-slate-100 text-[10px] font-black uppercase tracking-[0.2em] transition-all bg-white px-8 py-4 rounded-2xl shadow-sm border border-slate-100">
            <Download className="w-4 h-4 text-orange-600" />
            Télécharger les mentions (PDF)
          </button>
        </div>

      </div>
    </div>
  );
};

export default MentionsLegales;
